import { useFetch } from "@/lib/axios.config.ts";
import { TaskList } from "@/pages/get-don/tasks.types";
import { Story } from "@/shared/types/common";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

interface AppSettingsResponse {
  instagram_link: string;
  telegram_channel_link: string;
  // Referral reward tiers — single source of truth lives in the backend
  // AppSettings; the frontend must read these instead of hardcoding amounts.
  referral_1_100: string;
  referral_101_1000: string;
  referral_1000: string;
  profile_completion_award: string;

  facebook_link: string;
  twitter_link: string;
  website_link: string;
  youtube_link: string;
}

const useFetchSettings = () => {
  return useQuery({
    queryKey: ["settings"],
    queryFn: async () =>
      (await useFetch.get<AppSettingsResponse>("/common/AppSettings")).data,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

const useNavigation = () => {
  return useQuery({
    // Distinct key from useHomePage (which also used "navigation"). This hook
    // hits the UNCACHED /common/HomePageWidgetList so admin widget changes
    // (staged releases) show up immediately, instead of the 24h-cached widgets
    // bundled in /homepage/Homepage.
    queryKey: ["homeWidgets"],
    queryFn: async () =>
      (await useFetch.get("/common/HomePageWidgetList")).data,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export type WeeklyAwards = {
  award: string;
  bonus: string;
};

interface HomepageResponse {
  last_daily_check: {
    date: string;
    day_number: number;
    award: string;
    weekly_awards: WeeklyAwards[];
    // Set when today's claim was a non-premium reset and the 24h
    // recovery window is still open. Used to render the pulsing alert
    // badge on the check-in card and reopen the StreakLostModal.
    was_reset?: boolean;
    previous_day_number?: number | null;
    recovery_available_until?: string | null;
    // A recovery shown before the reset is persisted. The user must activate
    // Premium and claim before the server-day cutoff, rather than calling
    // RecoverStreak against a row that does not exist yet.
    recovery_requires_check_in?: boolean;
    // Authoritative Asia/Tashkent state from the backend. Device timezones are
    // not reliable for deciding whether a user can claim again.
    is_claimed?: boolean;
    next_check_in_at?: string | null;
    // A Premium user missed exactly one day and can keep their streak by
    // claiming today. Used to show the one-time streak-protection sheet.
    premium_grace_available?: boolean;
    premium_grace_missed_date?: string | null;
  } | null;
  stories: Story[];
  tasks: TaskList[];
  widgets: any[];
}

const useHomePage = () => {
  return useQuery({
    queryKey: ["navigation"],
    queryFn: async () => {
      const { data } = await useFetch.get<HomepageResponse>(
        "/homepage/Homepage",
        {
          params: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        }
      );
      return data;
    },
  });
};

const useMainStories = () => {
  return useQuery({
    queryKey: ["mainStories"],
    queryFn: async () =>
      (await useFetch.get<Story[]>("/common/StoryList?show_in_home=true")).data,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useStoryDetail = (slug: string | null) => {
  return useQuery({
    queryKey: ["storyDetail", slug],
    queryFn: async () =>
      (await useFetch.get<Story>(`/common/StoryDetail/${slug}`)).data,
    enabled: !!slug,
  });
};

const useStorySeenMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (storyId: string) =>
      (await useFetch.post(`/common/StorySeen/${storyId}`)).data,
    mutationKey: ["storySeen"],
    onSuccess: (_, storyId) => {
      const previewStory = queryClient.getQueryData<HomepageResponse>([
        "navigation",
      ]);
      if (!previewStory) return;
      const updatedStory = previewStory.stories.map((story) => {
        if (story.id === storyId) {
          return {
            ...story,
            is_seen: true,
          };
        }
        return story;
      });

      queryClient.setQueryData<HomepageResponse>(["navigation"], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          stories: updatedStory,
        };
      });
    },
    onError: (error) => {
      console.error("Error marking story as seen:", error);
    },
  });
};

export {
  useFetchSettings,
  useNavigation,
  useMainStories,
  useStorySeenMutation,
  useHomePage,
};
